import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { positionMenuByTrigger, positionMenuAtPoint } from '../../utils/menuPosition';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Dropdown } from 'react-bootstrap';
import { useToast } from '../../contexts/ToastContext';
import { track as trackEvent } from '../../utils/analytics';
import { loadCloudPlaylistFiles } from '../../utils/cloudPlaylistTracks';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { confirmLargeSet, confirmLargeLoad, LOAD_WARN_COUNT, SET_COST } from '../../utils/largeSetGuard';
import { useTierGate } from '../../hooks/useTierGate';
import ProgressBarWithStop from '../ProgressBarWithStop.js';
import ColoredWaveform from '../ColoredWaveform';
import CueList from '../CueList';
import CuePointsSettings from '../cuePointsSettings';
import CueSettingsPopoverV2 from './CueSettingsPopoverV2';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader from '../v2/ToolHeader';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import AddToPlaylistModal from '../AddToPlaylistModal';
import useRowSelection from '../../hooks/useRowSelection';
import CoverPlayButton from '../CoverPlayButton';
import { useEnrichedTracks, getEnrichedParts } from '../../hooks/useEnrichedTracks';
import { usePlayerRowSync } from '../../hooks/usePlayerRowSync';
import { setLocalListContext } from '../../utils/localListContext';
import { supportedFormats } from '../../utils/supportedFileFormats';
import { selectCueTargets } from '../../redux/slices/cuePointsSettingsSlice';
import { List } from 'react-window';
import { remainingForRun } from '../../utils/cueRunPlan';
import {
    setCueFiles, resetCueFiles, setSelectedCueFile, setBatchResults, setCuesForFile,
    addCue, updateCue, removeCue, removeCueFile,
    setCueIsProcessing, setCueProgress, setCueProgressMessage, setCueCancelRequested,
    undoCues, redoCues, pushCueUndo,
    setShownVariant, setTimelineOffset,
} from '../../redux/slices/cuePointsSlice';
import { addTrack, setAutoPlay, resetAudioList } from '../../redux/slices/musicPlayerSlice';
import { addToNextUp } from '../../utils/queueAdd';
import { resolveCover } from '../../utils/cover';
import { assignSharedHotcueSlots } from '../../../backend/migration/model';
import { ReactComponent as OpenIcon } from '../../assets/icons/open.svg';
import { ReactComponent as AudioFileIcon } from '../../assets/icons/audio-file.svg';
import { ReactComponent as DirectoryIcon } from '../../assets/icons/select-folder.svg';
import { ReactComponent as AnalyzeIcon } from '../../assets/icons/analyze-files.svg';
import { ReactComponent as SaveIcon } from '../../assets/icons/fixed-file.svg';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { ReactComponent as LoopIcon } from '../../assets/icons/loop.svg';
import './SmartRecsTab.scss';
import './CuePointsTab.scss';
import { errMsg } from '../../redux/utils/errMsg';

// Small marker glyph for the cue count (no dedicated cue icon in the set) —
// a filled point, matching how cues read as points on the waveform.
const CueGlyph = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
        <circle cx="12" cy="12" r="5" />
    </svg>
);

const HOT_COLORS = ['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#00C7BE', '#0A84FF', '#AF52DE', '#FF2D92'];
// Loop pad palette — cyan/teal shades, consistent with the app accent. Loops
// have their own 0..7 slot space (Serato/Rekordbox keep cue vs loop pads apart).
const LOOP_COLORS = ['#00BCD4', '#26C6DA', '#00ACC1', '#4DD0E1', '#0097A7', '#00BCD4', '#26C6DA', '#00ACC1'];

const MERGE_MS = 2000; // cues closer than this are treated as the same event
const isLoop = (c) => c.type === 'loop';

let _cueIdSeq = 1;
const withIds = (cues) => (cues || []).map((c) => ({ ...c, id: c.id != null ? c.id : _cueIdSeq++ }));

// Normalize cues/loops read from a file: ensure unique slots WITHIN each type
// (cues and loops have separate 0..N slot spaces), demoting collisions /
// out-of-range to memory. Loops keep a color even without a slot.
const sanitizeExisting = (cues, maxHot) => {
    const usedCue = new Set();
    const usedLoop = new Set();
    return [...(cues || [])].sort((a, b) => a.positionMs - b.positionMs).map((raw) => {
        // A zero-length "loop" (end missing / <= start) is a marker, not a loop
        // — keep it as a memory cue. The backend merge already does this; this
        // is the belt for any other path that feeds cues in.
        const degenerate = isLoop(raw) && !(typeof raw.endPositionMs === 'number' && raw.endPositionMs > raw.positionMs);
        const c = degenerate ? { ...raw, type: 'cue', endPositionMs: null, hotcueIndex: -1, color: null } : raw;
        const loop = isLoop(c);
        const used = loop ? usedLoop : usedCue;
        const maxSlots = loop ? LOOP_COLORS.length : maxHot;
        const palette = loop ? LOOP_COLORS : HOT_COLORS;
        let idx = c.hotcueIndex;
        if (idx == null || idx < 0 || idx >= maxSlots || used.has(idx)) idx = -1;
        if (idx >= 0) used.add(idx);
        const color = idx >= 0 ? (c.color || palette[idx]) : (loop ? (c.color || palette[0]) : null);
        return { ...c, hotcueIndex: idx, type: c.type || 'cue', color };
    });
};

// Smart merge (option A) for ONE type (cues OR loops): re-running REPLACES that
// type's previously auto-detected items but KEEPS manual + from-file items. New
// auto items fill free slots, skipping any near a kept item. A type that wasn't
// (re)generated is left completely untouched.
const mergeByType = (existSame, genSame, maxSlots, palette, dropOnFull) => {
    if (genSame.length === 0) return existSame; // not regenerated — leave intact
    const keep = existSame.filter((c) => c.source !== 'auto'); // drop old auto of this type
    const gen = genSame.map((c) => ({ ...c, source: 'auto' }));
    if (keep.length === 0) return gen; // fresh — backend already numbered by position
    const result = [...keep];
    const used = new Set(keep.filter((c) => c.hotcueIndex >= 0).map((c) => c.hotcueIndex));
    const free = [];
    for (let i = 0; i < maxSlots; i++) if (!used.has(i)) free.push(i);
    const near = (ms) => result.some((c) => Math.abs(c.positionMs - ms) <= MERGE_MS);
    const sorted = [...gen].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    for (const g of sorted) {
        if (near(g.positionMs)) continue;
        if (free.length) { const idx = free.shift(); result.push({ ...g, hotcueIndex: idx, color: palette[idx] }); }
        else if (!dropOnFull) { result.push({ ...g, hotcueIndex: -1, color: g.color || palette[0] }); }
    }
    return result;
};

// Grounded labels for cues: only distinctive STRUCTURAL kinds (from the loop
// detector), and only when a detected loop actually lands on the cue. We never
// invent structure the detector can't see, and never overwrite an existing name.
const STRUCT_KIND_LABEL = { drop: 'Drop', break: 'Break', intro: 'Intro', outro: 'Outro' };
const LABEL_NEAR_MS = 1500; // loops seed from cue positions, so starts ~coincide
const labelCuesFromLoops = (cues) => {
    const arr = cues || [];
    const structLoops = arr.filter((c) => isLoop(c) && STRUCT_KIND_LABEL[c.kind]);
    if (structLoops.length === 0) return arr;
    return arr.map((c) => {
        if (isLoop(c) || (c.name && c.name.trim())) return c;
        const hit = structLoops.find((l) => Math.abs(l.positionMs - c.positionMs) <= LABEL_NEAR_MS);
        return hit ? { ...c, name: STRUCT_KIND_LABEL[hit.kind] } : c;
    });
};

// A loop that starts "at" a cue must start EXACTLY on it — to the millisecond.
// Drift creeps in from older saves (pre-fix onset snapping was ±100ms) and from
// cross-source merges; visually/audibly a loop 90ms off its cue reads as off-beat.
// Window is tight (250ms): real artifacts are <100ms, and it can never grab an
// unrelated cue (MERGE_MS keeps distinct cues ≥2s apart). Loop LENGTH is
// preserved (end shifts by the same delta) so the wrap stays the same bars.
const ALIGN_LOOP_MS = 250;
export const alignLoopsToCues = (cues) => {
    const arr = cues || [];
    const cuePoints = arr.filter((c) => !isLoop(c));
    if (cuePoints.length === 0) return arr;
    return arr.map((c) => {
        if (!isLoop(c)) return c;
        let best = null;
        for (const q of cuePoints) {
            const d = q.positionMs - c.positionMs;
            if (Math.abs(d) <= ALIGN_LOOP_MS && (best === null || Math.abs(d) < Math.abs(best))) best = d;
        }
        if (best === null || best === 0) return c;
        return {
            ...c,
            positionMs: c.positionMs + best,
            endPositionMs: c.endPositionMs != null ? c.endPositionMs + best : c.endPositionMs,
        };
    });
};

// Re-assign the CUE slots (not loops) of an existing set to match the chosen
// mode — used to make the "Save cues as" toggle instantly reflect in the view:
//   'memory' -> every cue becomes a memory marker (no pads).
//   'hot'    -> the top-`maxHot` by confidence go on pads (numbered by position),
//               the rest become memory. Loops are left untouched (own slot space).
const reassignCueMode = (cues, mode, maxHot) => {
    const arr = cues || [];
    const loops = arr.filter(isLoop);
    const nonLoops = arr.filter((c) => !isLoop(c));
    if (mode === 'memory' || maxHot <= 0) {
        return labelCuesFromLoops([...nonLoops.map((c) => ({ ...c, hotcueIndex: -1, color: null })), ...loops]);
    }
    const hotSet = new Set(
        [...nonLoops].sort((a, b) => (b.confidence || 0) - (a.confidence || 0)).slice(0, maxHot)
    );
    let n = 0;
    const assigned = [...nonLoops]
        .sort((a, b) => a.positionMs - b.positionMs)
        .map((c) => {
            if (hotSet.has(c)) { const idx = n++; return { ...c, hotcueIndex: idx, color: HOT_COLORS[idx % HOT_COLORS.length] }; }
            return { ...c, hotcueIndex: -1, color: null };
        });
    return labelCuesFromLoops([...assigned, ...loops]);
};

// Merge cues and loops independently (each in its own slot space), then combine.
const mergeCues = (existing, generated, maxHot, enableMemory) => {
    const ex = existing || [];
    const gen = generated || [];
    const cues = mergeByType(ex.filter((c) => !isLoop(c)), gen.filter((c) => !isLoop(c)), maxHot, HOT_COLORS, !enableMemory);
    const loops = mergeByType(ex.filter(isLoop), gen.filter(isLoop), LOOP_COLORS.length, LOOP_COLORS, false)
        // Recolor every loop from the renderer palette so auto + manual loops look
        // consistent (the backend uses pure platform colors for fresh results).
        .map((l) => ({ ...l, color: l.hotcueIndex >= 0 ? LOOP_COLORS[l.hotcueIndex] : (l.color || LOOP_COLORS[0]) }));
    return [...cues, ...loops];
};

// The file list is virtualised (react-window, as the Library): only the rows on
// screen exist, so a store update costs the same for 40 tracks as for 4,000.
// Before this every row was mounted and each streamed cue read re-rendered all
// of them — 300–400 ms per file at 4.7k rows, a two-minute freeze on "Open all".
// rowHeight must match `.cue-file-row` in CuePointsTab.scss.
const CUE_ROW_HEIGHT = 42;

// One row of the file list. Module scope so react-window keeps a stable
// component identity; everything it reads arrives through rowProps.
const CueFileRow = ({
    index, style, ariaAttributes, files, cuesByFile, errorsByFile, selectedFile, selectedRows,
    readingFiles, analyzingFiles, anyRowCounts, displayName, enrichedMap, gridRepairByFile,
    playingPath, rowIsPlaying, rowCtx, menuTrackFor, onRowClick, onRowContextMenu, onRowPlay,
    onAddToQueue, onRemove,
}) => {
    const file = files[index];
    const cues = cuesByFile[file] || [];
    const cueN = cues.filter((c) => !isLoop(c)).length;
    const loopN = cues.length - cueN;
    const err = errorsByFile[file];
    const isSel = selectedFile === file;
    // read in flight (saved cues) or queued in a Generate run — hold the count
    // slots with a quiet pulse so counts replace it in place instead of popping in
    const pending = (readingFiles.has(file) || analyzingFiles.has(file)) && !cues.length;
    return (
        <div
            {...ariaAttributes}
            style={style}
            data-rowkey={file}
            className={`cue-file-row ${isSel ? 'selected' : ''} ${selectedRows.has(file) ? 'multi-selected' : ''} ${err ? 'has-error' : ''}`}
            onClick={(e) => onRowClick(index, file, e)}
            // Shift-click extends the selection — suppress the browser's
            // native shift-click text-selection so the two don't fight.
            onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
            onContextMenu={(e) => onRowContextMenu(file, e)}
            onDoubleClick={(e) => onRowPlay(file, e)}
            title={file}
        >
            <CoverPlayButton
                filePath={file}
                cover={getEnrichedParts(enrichedMap, file).cover}
                isPlaying={playingPath === file && rowIsPlaying}
                onClick={(e) => onRowPlay(file, e)}
            />
            {anyRowCounts && (
                <span className="cue-file-counts">
                    {err ? (
                        <span className="cue-count error">Error</span>
                    ) : pending ? (
                        <>
                            <span className="cue-count cues"><span className="cue-count-skeleton" /></span>
                            <span className="cue-count loops" />
                        </>
                    ) : (
                        <>
                            {/* Both slots always render (fixed width) so the cue
                                and loop columns line up across rows — empty when 0. */}
                            <span className="cue-count cues" title={cueN > 0 ? `${cueN} ${cueN === 1 ? 'cue' : 'cues'}` : undefined}>
                                {cueN > 0 && <><CueGlyph className="cue-count-ico" />{cueN}</>}
                            </span>
                            <span className="cue-count loops" title={loopN > 0 ? `${loopN} ${loopN === 1 ? 'loop' : 'loops'}` : undefined}>
                                {loopN > 0 && <><LoopIcon className="cue-count-ico" />{loopN}</>}
                            </span>
                        </>
                    )}
                </span>
            )}
            <span className="cue-file-name">{displayName(file)}</span>
            {gridRepairByFile[file] && (
                <span
                    className="cue-grid-fixed"
                    title={`${gridRepairByFile[file].app === 'serato' ? 'Serato' : 'Rekordbox'}'s beatgrid for this track sits ${Math.round(gridRepairByFile[file].median_off_ms)} ms off the beat. Loops were placed on the detected beats instead (same bar phase).`}
                >
                    grid fixed
                </span>
            )}
            <RowActions
                track={menuTrackFor(file)}
                onAddToQueue={() => onAddToQueue(file)}
                onRemoveFromLibrary={() => onRemove([file], 'menu')}
                removeLabel="Remove from list"
                openAtPoint={rowCtx && rowCtx.key === file ? rowCtx : null}
                desktopLike
                analyticsFrom="cue_points"
            />
        </div>
    );
};

// Ledger slot for the Serato GEOB tags written into the audio files themselves.
// They're a save destination like any DJ app (and are written even when no app
// is ticked), so they get tracked the same way.
export const FILE_TAGS = 'file-tags';

// Everything that decides what a save actually writes for one track: the cues
// themselves plus the settings that move them between pads, memory and the
// [cues] copy. Compared per destination, so a target that never received this
// exact state is still owed a write.
export const cueSaveSignature = (cues, settings = {}) => JSON.stringify([
    settings.loopMode, settings.autoActiveLoop, settings.keepOriginalsUntouched,
    (cues || []).map((c) => [
        c.positionMs, c.endPositionMs ?? null, c.type || '', c.hotcueIndex ?? -1,
        c.color || '', c.label || '', c.activeLoop === true ? 1 : 0,
    ]),
]);

// Does this track still owe a write to any selected destination?
// `savedSlots` is the per-target ledger for the file ({} / undefined = never
// saved), `edited` whether the user changed it since. A file that is neither
// edited nor previously saved is NOT a candidate: loading 500 library tracks
// and pressing Save must not rewrite all 500, and a never-touched track with no
// cues must never go out as "clear my cues".
export const fileNeedsSave = ({ sig, savedSlots, edited, saveTargets }) => {
    if (!savedSlots && !edited) return false;
    return (saveTargets || []).some((t) => (savedSlots || {})[t] !== sig);
};

const CuePointsTab = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { canAccess, requireUpgrade } = useTierGate('cue-points');

    const {
        cueFiles, selectedFile, cuesByFile, durationsByFile, errorsByFile, isProcessing, progress, progressMessage,
    } = useSelector((state) => state.cuePoints);
    const settings = useSelector((state) => state.cuePointsSettings);
    const targets = useSelector(selectCueTargets);
    // DESIGN V2 — tool header above the list, settings in its popover, the
    // zoom chip in the editor toolbar, a two-tone waveform. Gated; the old
    // structure is untouched while it is off.
    const v2 = useDesignV2();
    const zoomSlotRef = useRef(null);
    // The old settings panel persists the slice on every change; under v2 it
    // is not mounted, so the tab does it (same key, same shape).
    useEffect(() => {
        if (!v2) return;
        try { localStorage.setItem('cuePointsSettings_v11', JSON.stringify(settings)); } catch (e) { /* noop */ }
    }, [v2, settings]);
    const autoPlay = useSelector((state) => state.musicPlayer.autoPlay);
    // Signed-in users' playlists live in the cloud — the picker lists those
    // and resolves their tracks to local files (utils/cloudPlaylistTracks).
    const accessToken = useSelector((state) => state.auth.accessToken);
    const useServer = !!accessToken;

    const [src, setSrc] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [resolving, setResolving] = useState(false);
    // Source identity guard (same as FixMyTracksTab): a Generate click racing
    // an in-flight playlist/folder load must not run the PREVIOUS file set.
    const sourceEpochRef = useRef(0);
    const [playingPath, setPlayingPath] = useState(null);
    // Player-truth playing flag for the ROW icons (the waveform transport keeps
    // using redux autoPlay). The hook also moves playingPath to the next row on
    // list auto-advance, and clears it when a cloud track takes the player.
    const [rowIsPlaying, setRowIsPlaying] = useState(false);
    usePlayerRowSync(playingPath, setRowIsPlaying, setPlayingPath);
    const [metaByFile, setMetaByFile] = useState({});
    // Per-file save tracking. Save writes ONLY the tracks that still need it
    // (so editing one track doesn't rewrite every loaded track's cues), and a
    // file whose cues were all deleted still saves (as "clear my cues").
    // Two separate signals, because "changed" and "written" aren't the same:
    //   editedFiles — user edits since the last save; drives the discard prompt.
    //   savedSigs[file][target] — what was last written to THAT destination, so
    //     ticking a DJ app that wasn't ticked before (or changing a setting that
    //     moves where cues land) re-saves tracks that are otherwise unchanged
    //     instead of dead-ending on "No changes since the last save".
    const [editedFiles, setEditedFiles] = useState(() => new Set());
    const [savedSigs, setSavedSigs] = useState(() => ({}));
    const dirty = editedFiles.size > 0;
    const markDirty = useCallback((files) => {
        const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
        if (!list.length) return;
        setEditedFiles((prev) => { const next = new Set(prev); list.forEach((f) => next.add(f)); return next; });
    }, []);
    // Files whose embedded cues have already been read back, so no track costs
    // more than one read per loaded set.
    const ingestedRef = useRef(new Set());
    // Files whose embedded-cue read is currently in flight (a Python subprocess
    // per file). Drives the "reading cues…" skeleton so a track that hasn't
    // resolved yet never flashes the "No cues or loops yet" empty state.
    const [readingFiles, setReadingFiles] = useState(() => new Set());
    const markReading = useCallback((files, on) => {
        const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
        if (!list.length) return;
        setReadingFiles((prev) => {
            const next = new Set(prev);
            list.forEach((f) => (on ? next.add(f) : next.delete(f)));
            return next;
        });
    }, []);
    // Tracks a Generate run hasn't reached yet — its own set, so a saved-cue
    // read finishing for a file mid-run doesn't clear the run's pulse on it.
    const [analyzingFiles, setAnalyzingFiles] = useState(() => new Set());
    const markAnalyzing = useCallback((files, on) => {
        const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
        if (!list.length) return;
        setAnalyzingFiles((prev) => {
            const next = new Set(prev);
            list.forEach((f) => (on ? next.add(f) : next.delete(f)));
            return next;
        });
    }, []);
    // Size of the saved-cue read in flight (its peak since the set was last
    // empty) — the toolbar shows "Reading saved cues · done of total" from it.
    const [readTotal, setReadTotal] = useState(0);
    useEffect(() => {
        setReadTotal((t) => (readingFiles.size === 0 ? 0 : Math.max(t, readingFiles.size)));
    }, [readingFiles]);
    // A different set of files makes the ledger meaningless — drop it, and let
    // the new set be read from disk again.
    const clearDirty = useCallback(() => {
        setEditedFiles(new Set());
        setSavedSigs({});
        ingestedRef.current = new Set();
    }, []);
    const [showCues, setShowCues] = useState(true);   // independent layer toggles
    const [showLoops, setShowLoops] = useState(true);
    const canUndo = useSelector((s) => (s.cuePoints.past || []).length > 0);
    const canRedo = useSelector((s) => (s.cuePoints.future || []).length > 0);
    // Cue/loop positions live on the DJ apps' decoder timeline (what Rekordbox
    // and Serato store); the waveform and the player decode gapless, up to
    // 51 ms EARLIER on LAME MP3s (scripts/mp3_timeline.py). Per-file offset,
    // reported by the cue readers and by Generate; subtracted to draw/play.
    const timelineOffsetByFile = useSelector((s) => s.cuePoints.timelineOffsetByFile || {});
    // Tracks whose Rekordbox/Serato grid Generate found off the beat and
    // repaired (loops on the detected beats, the app's bar phase kept).
    const gridRepairByFile = useSelector((s) => s.cuePoints.gridRepairByFile || {});
    const [activeLoopId, setActiveLoopId] = useState(null); // loop currently looping in the player
    const [hoveredId, setHoveredId] = useState(null); // cue/loop hovered in pads OR on the waveform
    const enrichedMap = useEnrichedTracks(); // real covers for the play buttons
    // Playlist source picker (mirrors the other Prepare tabs' Open > Playlist).
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = useState(false);
    const [playlistList, setPlaylistList] = useState([]);
    const [playlistsLoading, setPlaylistsLoading] = useState(false);
    const [playlistSearch, setPlaylistSearch] = useState('');
    const [playlistDropdownPos, setPlaylistDropdownPos] = useState({ top: 0, left: 0, width: 250 });
    const playlistDropdownRef = useRef(null);
    const playlistBtnRef = useRef(null);
    const playlistSearchRef = useRef(null);
    // Flip the "Open ▸ Playlist" picker above its button when there's no room below.
    useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistDropdownRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(playlistDropdownRef.current, rect, { gap: 2, align: null });
        setPlaylistDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);
    const cancelGenRef = useRef(false); // set on Stop; checked in the async completion callback (redux value would be stale there)
    const loopPosRef = useRef(0); // live playback position inside the active loop (sec)
    const audioLatencyRef = useRef(0); // measured output latency (sec) for seam-free handoff
    const exitIntentRef = useRef('track'); // how a loop is being left: resume | seek | track

    // Normal playback uses the app's bottom player (consistent with the rest of the
    // app); Web Audio (in ColoredWaveform) handles only the gapless loop.
    const isActiveTrack = !!selectedFile && playingPath === selectedFile;
    const isPlaying = isActiveTrack && autoPlay;

    const selectedCues = useMemo(
        () => (selectedFile ? (cuesByFile[selectedFile] || []) : []),
        [selectedFile, cuesByFile]
    );

    // Latest cues/settings, readable from async work without re-running it on
    // every edit.
    const cuesByFileRef = useRef(cuesByFile);
    useEffect(() => { cuesByFileRef.current = cuesByFile; }, [cuesByFile]);
    const settingsRef = useRef(settings);
    useEffect(() => { settingsRef.current = settings; }, [settings]);

    // Auto-select the first file whenever the list changes and nothing is selected.
    useEffect(() => {
        if (cueFiles.length > 0 && (!selectedFile || !cueFiles.includes(selectedFile))) {
            dispatch(setSelectedCueFile(cueFiles[0]));
        }
        if (cueFiles.length === 0 && selectedFile) {
            dispatch(setSelectedCueFile(null));
        }
    }, [cueFiles, selectedFile, dispatch]);

    // ---- Multi-row selection + right-click menus on the file list ----
    // Finder semantics via the shared hook. The `selected` class (loaded
    // track / waveform) is separate from the multi-select highlight: plain
    // click and plain arrow both load the track AND select its row.
    const [rowCtx, setRowCtx] = useState(null);   // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = useState(null); // { x, y }
    const ctxNonceRef = useRef(0);
    // Bulk "Add N Tracks to Playlist" picker targets.
    const [bulkPlaylistTargets, setBulkPlaylistTargets] = useState([]);

    // Remove rows from the SESSION list only (loaded cue files) — nothing is
    // deleted from disk or the local DB, so no undo toast.
    const removeFilesFromList = useCallback((paths, via) => {
        if (!paths?.length) return;
        paths.forEach((p) => dispatch(removeCueFile(p)));
        trackEvent('tracks_removed_from_list', { tab: 'cue_points', count: paths.length, via });
    }, [dispatch]);

    const cueKeyIndex = useMemo(() => new Map(cueFiles.map((k, i) => [k, i])), [cueFiles]);
    const listContainerRef = useRef(null);
    const cueListRef = useRef(null);   // react-window's imperative handle
    const scrollRowIntoView = useCallback((index) => {
        cueListRef.current?.scrollToRow({ index, align: 'auto', behavior: 'instant' });
    }, []);
    const onDeleteSelected = useCallback((paths) => removeFilesFromList(paths, 'delete_key'), [removeFilesFromList]);
    const onPrimaryRow = useCallback((key) => dispatch(setSelectedCueFile(key)), [dispatch]);
    const {
        selected: selectedRows,
        orderedSelected,
        handleRowClick,
        selectOnly,
    } = useRowSelection({ keys: cueFiles, onDelete: onDeleteSelected, onArrowFocus: scrollRowIntoView, onPrimaryChange: onPrimaryRow });

    const handleRowContextMenu = (file, e) => {
        e.preventDefault();
        if (selectedRows.size > 1 && selectedRows.has(file)) {
            setBulkCtx({ x: e.clientX, y: e.clientY });
            return;
        }
        selectOnly(file, cueKeyIndex.get(file));
        dispatch(setSelectedCueFile(file));
        setRowCtx({ key: file, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    // Toggling "Save cues as" (Hot ↔ Memory) instantly re-assigns the cues
    // already in the view for EVERY track (loops untouched), so the change is
    // visible immediately instead of only affecting the next Generate.
    const prevCueModeRef = useRef(settings.cueMode);
    useEffect(() => {
        if (prevCueModeRef.current === settings.cueMode) return;
        prevCueModeRef.current = settings.cueMode;
        const files = Object.keys(cuesByFile);
        if (files.length === 0) return;
        const maxHot = settings.cueMode === 'memory'
            ? 0
            : ((settings.maxHotCues >= 1 && settings.maxHotCues <= 8) ? settings.maxHotCues : 8);
        dispatch(pushCueUndo());
        dispatch(setBatchResults(
            files.map((filePath) => ({ filePath, cuePoints: reassignCueMode(cuesByFile[filePath], settings.cueMode, maxHot) }))
        ));
        markDirty(files); // pad assignments changed for every loaded track
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.cueMode]);

    // Artist/title for the rows. Tracks the local DB knows show its names at
    // once (no file read at all); the rest — folder/file picks outside the
    // library — are read from tags in chunks, list order first, so names fill
    // in from the top while the read goes on. Waits for the DB's answer: before
    // it, every track would look unknown and the whole list would be parsed.
    const [metaProgress, setMetaProgress] = useState(null);   // { done, total } while tags are read
    const metaReadRef = useRef({});   // tag reads so far, by path — kept across list edits
    useEffect(() => {
        let cancelled = false;
        if (!cueFiles.length) { metaReadRef.current = {}; setMetaByFile({}); setMetaProgress(null); return undefined; }
        if (!enrichedMap.loaded) return undefined;
        const known = metaReadRef.current;
        const need = cueFiles.filter((f) => !(enrichedMap.get(f) || {}).title && !known[f]);
        if (!need.length) { setMetaProgress(null); return undefined; }
        (async () => {
            const CHUNK = 60;
            setMetaProgress({ done: 0, total: need.length });
            for (let i = 0; i < need.length && !cancelled; i += CHUNK) {
                const chunk = need.slice(i, i + CHUNK);
                let meta = null;
                try { meta = await window.electron.cuePoints.getMeta(chunk); } catch (e) { /* fall back to filenames */ }
                if (cancelled) return;
                if (meta) {
                    Object.assign(metaReadRef.current, meta);
                    setMetaByFile((prev) => ({ ...prev, ...meta }));
                }
                setMetaProgress({ done: Math.min(i + CHUNK, need.length), total: need.length });
            }
            if (!cancelled) setMetaProgress(null);
        })();
        return () => { cancelled = true; };
    }, [cueFiles, enrichedMap]);

    // Resolve audio URL for the selected file.
    useEffect(() => {
        let cancelled = false;
        if (!selectedFile) { setSrc(null); return undefined; }
        (async () => {
            try {
                const url = await window.electron.getAudioUrl(selectedFile);
                if (!cancelled) setSrc(url);
            } catch (e) { if (!cancelled) setSrc(null); }
        })();
        return () => { cancelled = true; };
    }, [selectedFile]);

    // Map a read-back cue's `origin` onto the ledger slot for that destination.
    // 'file' means the audio file's own Serato tags; Serato's own library is a
    // different place entirely (~/Music/_Serato_) and is never read here.
    const seedSavedSigs = useCallback((filePath, cues) => {
        const origins = new Set((cues || []).map((c) => c.origin).filter(Boolean));
        if (!origins.size) return;
        const sig = cueSaveSignature(cues, settingsRef.current);
        const slots = {};
        if (origins.has('file')) slots[FILE_TAGS] = sig;
        ['rekordbox', 'traktor', 'virtualdj'].forEach((p) => { if (origins.has(p)) slots[p] = sig; });
        if (!Object.keys(slots).length) return;
        setSavedSigs((prev) => ({ ...prev, [filePath]: { ...(prev[filePath] || {}), ...slots } }));
    }, []);

    // Read cues already embedded in the files and load them (fire-and-forget).
    // One Python process reads the whole list (file tags + the DJ-app
    // libraries) and streams a result per file; a file is only ever read once.
    // Every loaded track is read — "Generate remaining" has to know which
    // tracks already carry cues, and the list is virtualised, so thousands of
    // rows cost nothing to hold. ~25 ms per file on an external drive: a
    // 4.7k-track library takes about two minutes, shown in the toolbar.
    const ingestExistingCues = useCallback((files) => {
        const maxHot = settings.maxHotCues;
        const todo = (files || []).filter((f) => f && !ingestedRef.current.has(f));
        if (!todo.length) return;
        todo.forEach((f) => ingestedRef.current.add(f));
        markReading(todo, true);
        // Load a batch result for one file into redux + the save ledger.
        const ingestOne = (f, cues, timelineOffsetMs) => {
            // The reader knows the file's DJ-app timeline offset even when it
            // holds no cues — keep it so manual edits on this file land right.
            if (timelineOffsetMs != null) dispatch(setTimelineOffset({ filePath: f, offsetMs: timelineOffsetMs }));
            // Generating while a read is still in flight must win — the
            // read is stale by then, and overwriting would throw away
            // freshly detected cues.
            if (!cues || !cues.length || (cuesByFileRef.current[f] || []).length) return;
            // cues already in the file are treated as "file" (kept on re-analyze)
            const tagged = alignLoopsToCues(sanitizeExisting(cues, maxHot).map((c) => ({ ...c, source: 'file' })));
            dispatch(setCuesForFile({ filePath: f, cuePoints: withIds(tagged) }));
            // Seed the save ledger from where these cues were actually
            // found, so Save knows what each destination already holds.
            // Serato is never a read source (its library is separate
            // from the file tags), so it stays owed a write until one
            // succeeds — which is the point: cues that exist in the
            // file and in rekordbox still have to be pushed to Serato.
            seedSavedSigs(f, tagged);
        };
        (async () => {
            // Streaming batch IPC: still one Python spawn + one library parse
            // for the whole list, but each file's cues arrive (and render) the
            // moment that file is read — track by track, not all-at-the-end.
            const streamApi = window.electron.cuePoints.readCuesStream;
            if (streamApi) {
                // Results are applied in batches (every ~150 ms), not one store
                // update per file: with the reader at 30–40 files a second that
                // is a handful of renders a second instead of dozens.
                const batch = [];
                let timer = null;
                const flush = () => {
                    timer = null;
                    if (!batch.length) return;
                    const done = batch.splice(0);
                    done.forEach(([f, cues, off]) => ingestOne(f, cues, off));
                    markReading(done.map(([f]) => f), false);
                };
                try {
                    await streamApi(todo, (f, cues, timelineOffsetMs) => {
                        batch.push([f, cues, timelineOffsetMs]);
                        if (!timer) timer = setTimeout(flush, 150);
                    });
                } catch (e) { /* ignore unreadable */ }
                finally {
                    if (timer) clearTimeout(timer);
                    flush();
                    markReading(todo, false); // clear any skeleton the stream missed
                }
                return;
            }
            // Older main process (no streaming handler): one spawn per CHUNK,
            // applied per chunk.
            const batchApi = window.electron.cuePoints.readCuesBatch;
            if (batchApi) {
                const CHUNK = 40;
                for (let i = 0; i < todo.length; i += CHUNK) {
                    const chunk = todo.slice(i, i + CHUNK);
                    try {
                        const byFile = await batchApi(chunk);
                        chunk.forEach((f) => ingestOne(f, byFile && byFile[f]));
                    } catch (e) { /* ignore unreadable */ }
                    finally { markReading(chunk, false); }
                }
                return;
            }
            // Older main process (no batch handler yet): per-file reads.
            for (const f of todo) {
                try {
                    ingestOne(f, await window.electron.cuePoints.readCues(f));
                } catch (e) { /* ignore unreadable */ }
                finally { markReading(f, false); }
            }
        })();
    }, [dispatch, settings.maxHotCues, seedSavedSigs, markReading]);

    // Whatever the eager pass skipped: read the selected track on demand, so a
    // track never reads as "no cues" just because it wasn't part of that pass.
    useEffect(() => {
        if (!selectedFile || (cuesByFileRef.current[selectedFile] || []).length) return;
        ingestExistingCues([selectedFile]);
    }, [selectedFile, ingestExistingCues]);

    // Confirm before discarding unsaved cues.
    const confirmDiscard = useCallback(async () => {
        if (!dirty || cueFiles.length === 0) return true;
        return toast.confirm(
            'You have unsaved cue points. Discard them?',
            'Unsaved cue points',
            { confirmText: 'Discard', confirmVariant: 'danger' }
        );
    }, [dirty, cueFiles.length, toast]);

    // Any change to the source value bumps the epoch — including dispatches
    // from OUTSIDE this tab (playlist sidebar "Open in tool"), which the
    // per-handler bumps can't see.
    const sourceFiles_watch = React.useRef(cueFiles);
    React.useEffect(() => {
        if (sourceFiles_watch.current !== cueFiles) {
            sourceFiles_watch.current = cueFiles;
            sourceEpochRef.current += 1;
        }
    }, [cueFiles]);

    // Expand a set of file/dir paths into audio files and load them.
    // append=true ADDS to the current list (deduped) instead of replacing it —
    // dropping a track onto a populated list means "add this one", not "start
    // over" (and must not wipe the cues already loaded for the other files).
    // Asks before loading more than LOAD_WARN_COUNT tracks: all, the first
    // LOAD_WARN_COUNT, or none (null). A 4.7k-track list took 2.7 s to show and
    // froze scrolling, and analysing it all is a ~16 h run.
    const gateLargeLoad = useCallback(async (list, source) => {
        const choice = await confirmLargeLoad(toast, list.length, {
            secondsPerFile: SET_COST.cuePoints, parallelism: 1, sourceLabel: source, tab: 'cue_points',
        });
        if (!choice) return null;
        return choice === 'first' ? list.slice(0, LOAD_WARN_COUNT) : list;
    }, [toast]);

    const loadPaths = useCallback(async (paths, { append = false, source = 'selection' } = {}) => {
        if (!paths || !paths.length) return;
        if (!append && !(await confirmDiscard())) return;
        setResolving(true);
        try {
            // Explicit files (playlist rows, multi-file picks) keep their
            // GIVEN order — playlist position is the set order. Only folder
            // selections are expanded and sorted by the DISPLAYED
            // "Artist - Title" label (path order is store-filename noise) —
            // same rule as the Compat/Loudness tabs.
            const isAudioPath = (x) => /\.(mp3|wav|wave|aif|aiff|flac|m4a|ogg|aac|alac)$/i.test(x);
            const hasDirs = paths.some((x) => !isAudioPath(x));
            let files;
            if (!hasDirs) {
                files = paths.filter(isAudioPath);
            } else {
                const resolved = await window.electron.cuePoints.resolveAudioFiles(paths);
                const byLabel = (a, b) => getEnrichedParts(enrichedMap, a).label
                    .localeCompare(getEnrichedParts(enrichedMap, b).label, undefined, { sensitivity: 'base', numeric: true });
                files = [...(resolved || [])].sort(byLabel);
            }
            if (!files || !files.length) { toast.warning('No audio files found'); return; }
            if (append && cueFiles.length) {
                const all = files.filter((f) => !cueFiles.includes(f));
                if (!all.length) { toast.info('Already in the list'); return; }
                const fresh = await gateLargeLoad(all, source);
                if (!fresh) return;
                sourceEpochRef.current += 1;
                dispatch(setCueFiles([...cueFiles, ...fresh]));
                ingestExistingCues(fresh);
            } else {
                files = await gateLargeLoad(files, source);
                if (!files) return;
                sourceEpochRef.current += 1;
                dispatch(resetCueFiles());
                dispatch(setCueFiles(files));
                clearDirty();
                // Read any cues already embedded in the files (so saved cues reappear).
                ingestExistingCues(files);
            }
        } catch (e) {
            toast.error('Failed to read files');
        } finally {
            setResolving(false);
        }
    }, [dispatch, toast, confirmDiscard, ingestExistingCues, cueFiles, enrichedMap, gateLargeLoad]);

    const handleChooseFiles = async () => {
        const res = await window.electron.cuePoints.openDialog('files');
        if (res.canceled) return; // canceling must NOT resurrect the old set
        await loadPaths(res.paths);
    };
    const handleChooseDir = async () => {
        const res = await window.electron.cuePoints.openDialog('dir');
        if (res.canceled) return;
        await loadPaths(res.paths, { source: 'folder' });
    };
    const handleChooseLibrary = async () => {
        if (!(await confirmDiscard())) return;
        try {
            const allTracks = await window.electron.db.tracks.getAll();
            const allPaths = (allTracks || [])
                .map((t) => t.file_path || t.filePath || t.file)
                .filter((f) => f && !f.startsWith('online://') && !f.startsWith('http'));
            if (!allPaths.length) { toast.warning('Your library is empty'); return; }
            const filePaths = await gateLargeLoad(allPaths, 'library');
            if (!filePaths) return;
            sourceEpochRef.current += 1;
            dispatch(resetCueFiles());
            dispatch(setCueFiles(filePaths));
            clearDirty();
            // Same as every other load path — without this, cues already saved
            // into these tracks read as "no cues" after opening the library.
            ingestExistingCues(filePaths);
        } catch (e) { toast.error('Failed to load library'); }
    };
    const handleClear = async () => {
        if (!(await confirmDiscard())) return;
        dispatch(resetCueFiles());
        clearDirty();
    };

    // ---- playlist source picker (Open > Playlist) ----
    const handlePlaylistDropdownToggle = async (anchorEl) => {
        if (playlistDropdownOpen) { setPlaylistDropdownOpen(false); return; }
        setPlaylistSearch('');
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setPlaylistDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 250) });
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            if (useServer) {
                const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                setPlaylistList(result?.data || []);
            } else {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                setPlaylistList(allPlaylists || []);
            }
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        setResolving(true);
        try {
            let filePaths = [];
            let total = 0;
            if (useServer) {
                // Cloud playlist — resolve rows to local files (exact link /
                // origin path, one-time heuristic backfill for legacy rows).
                const loaded = await loadCloudPlaylistFiles(accessToken, playlist.id);
                if (!loaded || loaded.total === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }
                total = loaded.total;
                filePaths = loaded.filePaths;
                if (filePaths.length === 0) {
                    toast.warning('None of this playlist’s tracks are on this device — these tools work on local audio files');
                    return;
                }
            } else {
                const playlistData = await window.electron.db.playlists.get(playlist.id, true);
                if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                    toast.warning('This playlist has no tracks');
                    return;
                }
                const tracks = playlistData.tracks.filter((t) => t.file || t.filePath);
                total = tracks.length;
                filePaths = tracks
                    .map((t) => t.file || t.filePath)
                    .filter((f) => f && !f.startsWith('online://') && !f.startsWith('http://') && !f.startsWith('https://'));
                if (filePaths.length === 0) {
                    toast.warning('This playlist only contains cloud tracks — no local files to load');
                    return;
                }
            }
            await loadPaths(filePaths, { source: 'playlist' });
            const skipped = total - filePaths.length;
            if (skipped > 0) toast.info(`Skipped ${skipped} ${skipped === 1 ? 'track' : 'tracks'} with no local file on this device`);
            trackEvent('playlist_opened_in_tool', {
                tab: 'cue-points',
                source: useServer ? 'cloud' : 'local',
                track_count: filePaths.length,
                skipped_count: skipped,
            });
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        } finally {
            setResolving(false);
        }
    };

    // Close playlist dropdown on outside click.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    // ---- drag & drop (files + folders) ----
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = async (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const paths = Array.from(e.dataTransfer.files || [])
            .map((f) => window.electron.getPathForFile(f))
            .filter(Boolean);
        await loadPaths(paths, { append: true });
    };

    // ---- detection ----
    // mode: 'cues' (CUE-DETR only) | 'both' (cues + loops in one analysis pass).
    // A single "Generate" runs whichever the Detect-loops setting selects.
    const runGenerate = async (files, mode = 'both', { remainingOnly = false, skippedCount = 0 } = {}) => {
        if (isProcessing || !files || files.length === 0) return;
        // Detection is free on every plan; runSave is the GOAT step.
        // Source captured at click time; abort if it changes during the gate
        // awaits below (see sourceEpochRef).
        const runEpoch = sourceEpochRef.current;
        // Time-based large-selection gate — CUE-DETR inference is sequential
        // (~seconds per track), so this warns much earlier than other tabs.
        if (!(await confirmLargeSet(toast, files.length, {
            secondsPerFile: SET_COST.cuePoints,
            parallelism: 1,
            sourceLabel: 'selection',
            tab: 'cue_points',
        }))) return;
        if (sourceEpochRef.current !== runEpoch) return;
        trackEvent('cue_points_generate', { count: files.length, mode, remaining_only: remainingOnly, skipped_count: skippedCount });
        const genStartTime = Date.now();

        // Files already applied from streamed per-track results (progress
        // callback) — the complete handler must not re-merge them.
        const appliedOnTheFly = new Set();
        let firstStreamedShown = false;

        cancelGenRef.current = false;
        dispatch(setCueIsProcessing(true));
        dispatch(setCueProgress(0));
        dispatch(setCueCancelRequested(false));
        dispatch(setCueProgressMessage(mode === 'loops' ? 'Analyzing beats…' : 'Loading model…'));
        // Rows without results yet pulse their count slots while the run works
        // through them (same skeleton the embedded-cue read uses) — each file's
        // pulse clears the moment its streamed result lands.
        markAnalyzing(files, true);

        // Resolve the cue-type choice into the (maxHot, memory-overflow) params.
        //   'memory' -> 0 hot cues, everything becomes a memory marker.
        //   'hot'    -> top-N on the pads, any extra kept as memory (never dropped).
        const cueMode = settings.cueMode || 'hot';
        const clampedMaxHot = (settings.maxHotCues >= 1 && settings.maxHotCues <= 8) ? settings.maxHotCues : 8;
        const effectiveMaxHot = cueMode === 'memory' ? 0 : clampedMaxHot;

        // For loops, seed every loop's START from the track's existing cue
        // positions so loops begin on a verified cue.
        const cuePositionsByFile = {};
        if (mode === 'loops') {
            for (const f of files) {
                cuePositionsByFile[f] = (cuesByFile[f] || [])
                    .filter((c) => !isLoop(c))
                    .map((c) => c.positionMs)
                    .sort((a, b) => a - b);
            }
        }

        // In every mode, tell the detector which cues will SURVIVE the merge
        // (file/manual cues beat a fresh detection within MERGE_MS), so loops
        // anchor to the cue the user will actually see — never to a fresh cue
        // that then loses the merge and leaves the loop floating beside a
        // different displayed cue.
        const existingCuePositionsByFile = {};
        for (const f of files) {
            existingCuePositionsByFile[f] = (cuesByFile[f] || [])
                .filter((c) => !isLoop(c) && c.source !== 'auto')
                .map((c) => c.positionMs)
                .sort((a, b) => a - b);
        }

        window.electron.cuePoints.autoGenerate(
            {
                files,
                targets: [],
                write: false,
                mode,
                cuePositionsByFile,
                existingCuePositionsByFile,
                sensitivity: settings.sensitivity,
                minDistanceBars: settings.minDistanceBars,
                maxHotCues: effectiveMaxHot,
                // Hot mode = clean top-N pads (overflow dropped). Memory mode =
                // everything becomes a memory marker (maxHot is 0 there).
                enableMemoryCues: cueMode === 'memory',
                loopBars: settings.loopBars,
                maxLoops: (settings.maxLoops >= 1 && settings.maxLoops <= 8) ? settings.maxLoops : 6,
                loopSensitivity: settings.loopSensitivity,
                loopFocus: settings.loopFocus,
                snapToBeat: settings.snapToBeat,
                // Dark setting (no UI): snap detected cues onto the beatgrid.
                quantizeCuesToGrid: settings.quantizeCuesToGrid === true,
                // Experimental downbeat vote (dev flag): primary placement and,
                // when comparing, both placements from the same analysis.
                downbeatMode: settings.downbeatMode || 'merged',
                downbeatVariants: settings.downbeatCompare ? ['merged', 'kick'] : undefined,
            },
            (p) => {
                if (p.progressPercentage != null) dispatch(setCueProgress(p.progressPercentage));
                // A finished file streams in mid-run — apply it immediately so
                // tracks populate on the fly instead of all-at-the-end. The
                // complete handler skips whatever was applied here.
                if (p.trackResult && p.trackResult.filePath && !cancelGenRef.current) {
                    const r = p.trackResult;
                    appliedOnTheFly.add(r.filePath);
                    markAnalyzing(r.filePath, false);
                    // Live ref, not a stale closure: merge against whatever the
                    // user's edits have made of this track by now.
                    const existing = cuesByFileRef.current[r.filePath] || [];
                    const merged = r.error ? existing
                        : alignLoopsToCues(labelCuesFromLoops(mergeCues(existing, withIds(r.cuePoints), effectiveMaxHot, cueMode === 'memory')));
                    dispatch(setBatchResults([{ ...r, cuePoints: merged }]));
                    if (!r.error) {
                        if ((r.cuePoints || []).length) markDirty([r.filePath]);
                        if (r.gridRepair) trackEvent('cue_points_grid_repaired', { app: r.gridRepair.app, offset_ms: Math.round(r.gridRepair.median_off_ms || 0) });
                        // Jump to the first finished track once, so the results
                        // are visible while the rest still analyze (same track
                        // the complete handler used to select at the end).
                        if (!firstStreamedShown) { firstStreamedShown = true; dispatch(setSelectedCueFile(r.filePath)); }
                    }
                }
                if (p.file) dispatch(setCueProgressMessage(`Analyzing ${p.file} (${Math.min(p.index + 1, p.total)}/${p.total})`));
                else if (p.stage === 'loading_model') dispatch(setCueProgressMessage(mode === 'loops' ? 'Analyzing beats…' : 'Loading model…'));
            },
            (result) => {
                // Whatever the outcome, no row keeps pulsing.
                markAnalyzing(files, false);
                // Cancelled (locally via Stop, or reported by the backend after
                // it killed the inference): don't apply results or toast success.
                if (cancelGenRef.current || result.cancelled) {
                    dispatch(setCueIsProcessing(false));
                    dispatch(setCueProgress(0));
                    return;
                }
                dispatch(setCueIsProcessing(false));
                dispatch(setCueProgress(100));
                trackEvent('cue_points_generate_complete', { success: !result.error, track_count: files.length, duration_ms: Date.now() - genStartTime });
                if (result.error) { toast.error(`Detection failed: ${errMsg(result.error)}`); return; }
                // Merge generated items with existing ones — only the regenerated
                // type is replaced. Files that already streamed in mid-run were
                // applied by the progress callback; re-merging them here would
                // double-apply against their own output, so only the leftovers
                // (older backend without streaming, or missed events) run here.
                const leftovers = (result.results || []).filter((r) => !appliedOnTheFly.has(r.filePath));
                const withCueIds = leftovers.map((r) => {
                    const existing = cuesByFileRef.current[r.filePath] || [];
                    const generated = withIds(r.cuePoints);
                    return { ...r, cuePoints: alignLoopsToCues(labelCuesFromLoops(mergeCues(existing, generated, effectiveMaxHot, cueMode === 'memory'))) };
                });
                if (withCueIds.length) dispatch(setBatchResults(withCueIds));
                for (const r of leftovers) {
                    if (!r.error && r.gridRepair) trackEvent('cue_points_grid_repaired', { app: r.gridRepair.app, offset_ms: Math.round(r.gridRepair.median_off_ms || 0) });
                }
                // Count freshly generated cues vs loops for the toast (all files,
                // streamed and not).
                let cN = 0; let lN = 0;
                for (const r of (result.results || [])) {
                    for (const c of (r.cuePoints || [])) { if (c.type === 'loop') lN++; else cN++; }
                }
                const all = result.results || [];
                const failed = all.filter((r) => r.error).length;
                if (cN + lN > 0) markDirty(withCueIds.filter((r) => !r.error).map((r) => r.filePath));
                const parts = [];
                if (mode !== 'loops') parts.push(`${cN} ${cN === 1 ? 'cue' : 'cues'}`);
                if (mode !== 'cues') parts.push(`${lN} ${lN === 1 ? 'loop' : 'loops'}`);
                toast.success(`Generated ${parts.join(' · ')} across ${all.length - failed} tracks`);
                if (!firstStreamedShown) {
                    const firstOk = all.find((r) => !r.error);
                    if (firstOk) dispatch(setSelectedCueFile(firstOk.filePath));
                }
            }
        );
    };

    const wantCues = settings.detectCues !== false;
    const wantLoops = settings.detectLoops !== false;
    const genMode = wantCues && wantLoops ? 'both' : wantCues ? 'cues' : wantLoops ? 'loops' : null;
    // "Generate remaining": tracks that already hold what the run would add —
    // cues saved in the file / DJ app, or detected earlier this session — are
    // left alone, so a stopped run picks up where it left off and hand-prepared
    // tracks aren't redone. With nothing prepared, or nothing left, the button
    // runs them all. The per-row Generate always re-runs its one track.
    const remaining = useMemo(
        () => (genMode ? remainingForRun(cueFiles, cuesByFile, genMode) : cueFiles),
        [cueFiles, cuesByFile, genMode]
    );
    const generateAll = remaining.length === 0 || remaining.length === cueFiles.length;
    const handleGenerate = () => {
        if (!genMode) { toast.warning('Enable Detect cues or Detect loops in settings'); return; }
        const files = generateAll ? cueFiles : remaining;
        runGenerate(files, genMode, { remainingOnly: !generateAll, skippedCount: cueFiles.length - files.length });
    };
    const handleRowGenerate = (file, e) => {
        e.stopPropagation();
        if (!genMode) { toast.warning('Enable Detect cues or Detect loops in settings'); return; }
        dispatch(setSelectedCueFile(file));
        runGenerate([file], genMode);
    };

    // ---- what still needs writing, and where ----
    const saveTargets = useMemo(
        () => (settings.writeFileTags ? [...targets, FILE_TAGS] : [...targets]),
        [targets, settings.writeFileTags]
    );
    const cueSig = useCallback(
        (filePath) => cueSaveSignature(cuesByFile[filePath], settings),
        [cuesByFile, settings]
    );
    const needsSave = useCallback((f) => fileNeedsSave({
        sig: cueSig(f), savedSlots: savedSigs[f], edited: editedFiles.has(f), saveTargets,
    }), [cueSig, savedSigs, editedFiles, saveTargets]);

    // Save also works while a Generate run is still going through the list —
    // the tracks finished so far — so a 4,000-track run isn't all-or-nothing.
    // Mid-run the run keeps the progress bar and only the Save button reads
    // "Saving…"; idle, the bar shows the save as it always did.
    const [saving, setSaving] = useState(false);
    const runSave = async (files, { forced = false } = {}) => {
        if (!canAccess) { requireUpgrade(); return; }
        // Tracks whose cues were all deleted are included on purpose — an empty
        // cue list is a change too ("clear my cues"), and the writers replace.
        const tracksWithCues = files.map((f) => ({ filePath: f, cuePoints: cuesByFile[f] || [] }));

        const duringRun = isProcessing;
        setSaving(true);
        if (!duringRun) {
            dispatch(setCueIsProcessing(true));
            dispatch(setCueProgressMessage('Saving cues & loops…'));
        }
        try {
            const res = await window.electron.cuePoints.writeManual(tracksWithCues, {
                targets,
                keepOriginalsUntouched: settings.keepOriginalsUntouched,
                writeFileTags: settings.writeFileTags,
                loopMode: settings.loopMode,
                autoActiveLoop: settings.autoActiveLoop,
            });
            if (res.error) { toast.error(`Save failed: ${errMsg(res.error)}`); }
            else {
                // An XML fallback (method: 'xml') is NOT a library save — the DB
                // write failed and the cues landed in a rekordbox.xml the user
                // must import manually. Don't let it read as "saved to rekordbox".
                const okTargets = (res.targets || []).filter((t) => t.success && t.method !== 'xml');
                const xmlFallbacks = (res.targets || []).filter((t) => t.success && t.method === 'xml');
                const libs = okTargets.map((t) => t.platform);
                // Count what the user actually saved (tracks), not how many
                // files got tags — a clear-save (0 cues) is still 1 track.
                const nSaved = tracksWithCues.length;
                const nCleared = tracksWithCues.filter((t) => t.cuePoints.length === 0).length;
                let msg = `Saved ${nSaved} ${nSaved === 1 ? 'track' : 'tracks'}`;
                if (nCleared > 0) msg += ` (${nCleared === nSaved ? 'cues cleared' : `${nCleared} cleared`})`;
                if (libs.length) msg += ` to ${libs.join(', ')}`;
                toast.success(msg);
                // Placement summary — say exactly what landed where in
                // Rekordbox so there's nothing to reverse-engineer later
                // ("saved 12, only see 8 pads" reads as a bug otherwise).
                if (okTargets.some((t) => t.platform === 'rekordbox')) {
                    let padCues = 0, padLoops = 0, memCues = 0, memLoops = 0;
                    for (const t of tracksWithCues) {
                        const slots = assignSharedHotcueSlots(t.cuePoints, settings.loopMode);
                        t.cuePoints.forEach((c, i) => {
                            const loop = isLoop(c);
                            if (slots[i] != null) { loop ? padLoops++ : padCues++; }
                            else { loop ? memLoops++ : memCues++; }
                        });
                    }
                    const n = (count, word) => `${count} ${word}${count !== 1 ? 's' : ''}`;
                    const padPart = (padCues || padLoops)
                        ? `pads: ${[padCues ? n(padCues, 'cue') : null, padLoops ? n(padLoops, 'loop') : null].filter(Boolean).join(' + ')}`
                        : null;
                    const memPart = (memCues || memLoops)
                        ? `memory: ${[memCues ? n(memCues, 'cue') : null, memLoops ? n(memLoops, 'loop') : null].filter(Boolean).join(' + ')}`
                        : null;
                    if (padPart || memPart) {
                        toast.info(`Rekordbox — ${[padPart, memPart].filter(Boolean).join(' · ')}`, 6000);
                    }
                }
                // 9s (not the 3s default): these carry actionable instructions ("quit
                // Rekordbox completely, then save again") that need time to be read.
                // Matches the other DJ-app warnings (seratoRunning, sync notice).
                xmlFallbacks.forEach((t) => toast.warning(
                    `${t.platform}: couldn’t write the library directly — cues were exported to an XML file instead. Import it manually (rekordbox: Preferences > Advanced > rekordbox xml).`, 9000));
                (res.targets || []).filter((t) => !t.success).forEach((t) => toast.warning(t.appOpen ? t.error : `${t.platform}: ${t.error}`, 9000));
                // Serato caches cues while running and overwrites file tags on
                // exit — so it must be closed for the cues to stick. They then
                // show up automatically next time Serato opens (no re-import).
                if (res.seratoRunning) {
                    toast.warning('Serato is open — close it so your cues aren’t overwritten. They’ll show up next time you open Serato.', 9000);
                }
                // Record what actually landed, per destination. A failed target
                // (or an XML fallback, which the user still has to import) keeps
                // its old signature so the next Save retries just that one — and
                // anything written while Serato is running may be clobbered when
                // Serato exits, so those slots stay unstamped too.
                const savedTo = okTargets.map((t) => t.platform);
                const tagsOk = settings.writeFileTags && res.fileTags && !res.fileTags.error && !(res.fileTags.errors > 0);
                if (tagsOk) savedTo.push(FILE_TAGS);
                const stamped = res.seratoRunning
                    ? savedTo.filter((t) => t !== 'serato' && t !== FILE_TAGS)
                    : savedTo;
                trackEvent('cue_points_save', {
                    track_count: tracksWithCues.length, targets: stamped, forced, during_run: duringRun,
                });
                if (stamped.length) {
                    setSavedSigs((prev) => {
                        const next = { ...prev };
                        files.forEach((f) => {
                            const sig = cueSig(f);
                            const slots = { ...(next[f] || {}) };
                            stamped.forEach((t) => { slots[t] = sig; });
                            next[f] = slots;
                        });
                        return next;
                    });
                    // Edits are only "saved" once something accepted them.
                    setEditedFiles((prev) => {
                        const next = new Set(prev);
                        files.forEach((f) => next.delete(f));
                        return next;
                    });
                }
            }
        } catch (e) { toast.error(`Save failed: ${e.message}`); }
        finally {
            setSaving(false);
            if (!duringRun) dispatch(setCueIsProcessing(false));
        }
    };

    const handleSave = async () => {
        if (!saveTargets.length) {
            toast.warning('Nowhere to save to — pick a DJ app in settings, or turn on “Embed cue tags in the audio files”.', 6000);
            return;
        }
        // Mid-run: only what the run has finished — never a file it may still
        // be decoding.
        const pending = cueFiles.filter((f) => needsSave(f) && !(isProcessing && analyzingFiles.has(f)));
        if (pending.length) { await runSave(pending); return; }
        if (isProcessing) { toast.info('Nothing finished to save yet'); return; }

        // Nothing is owed to any selected destination — either it's all been
        // written already, or these tracks were only loaded, never touched.
        // Neither means the DJ app really holds these cues (Serato rewrites tags
        // on exit, a rekordbox DB can be restored from backup, cues get deleted
        // in the app itself), so offer the write instead of dead-ending.
        const candidates = cueFiles.filter((f) => (cuesByFile[f] || []).length > 0 || editedFiles.has(f));
        if (!candidates.length) { toast.info('No cue points to save yet'); return; }
        const where = saveTargets.filter((t) => t !== FILE_TAGS).join(', ') || 'the audio files';
        const again = await toast.confirm(
            `No changes to write to ${where}. Write ${candidates.length === 1 ? 'this track' : `all ${candidates.length} tracks`} again anyway? Use this to push existing cues into a DJ app you just added, or if the cues were changed or lost outside MixGoat.`,
            'Nothing new to save',
            { confirmText: 'Save anyway' }
        );
        if (!again) return;
        await runSave(candidates, { forced: true });
    };

    const handleStop = () => {
        cancelGenRef.current = true;
        dispatch(setCueCancelRequested(true));
        dispatch(setCueIsProcessing(false));
        window.electron.cuePoints.cancel();
    };

    // ---- playback via the app's global player (shows in the bottom player) ----
    const buildTrackObj = useCallback(async (file) => {
        const musicSrc = await window.electron.getAudioUrl(file);
        // Prefer the enriched (Mixpedia) cover; only fall back to embedded art.
        const enriched = getEnrichedParts(enrichedMap, file);
        let cover = enriched.cover;
        if (!cover) { try { cover = await window.electron.getAlbumArtUrl(file); } catch (e) {} }
        const meta = metaByFile[file];
        let name, artist = '';
        if (meta && meta.title) { name = meta.title; artist = meta.artist || ''; }
        else {
            const base = (file.split('/').pop() || '').replace(/\.[^/.]+$/, '');
            name = base;
            if (base.includes(' - ')) { const p = base.split(' - '); artist = p[0].trim(); name = p.slice(1).join(' - ').trim(); }
        }
        return { name, singer: artist, cover: resolveCover(cover), musicSrc };
    }, [metaByFile, enrichedMap]);

    // Sync title/artist for the row "…" menu (metaByFile first, else filename).
    // Includes filePath so Add-to-Playlist links the real local file.
    const menuTrackFor = useCallback((file) => {
        const meta = metaByFile[file];
        let title, artist = '';
        if (meta && meta.title) { title = meta.title; artist = meta.artist || ''; }
        else {
            const base = (file.split('/').pop() || '').replace(/\.[^/.]+$/, '');
            title = base;
            if (base.includes(' - ')) { const p = base.split(' - '); artist = p[0].trim(); title = p.slice(1).join(' - ').trim(); }
        }
        // Catalog cover + id from the local DB, so a Like from this row carries
        // real identity instead of bare text.
        const enriched = getEnrichedParts(enrichedMap, file);
        return { title, artist, filePath: file, artwork: enriched.cover || null, djpalId: enriched.djpalId || null };
    }, [metaByFile, enrichedMap]);

    // Play a track in the app's bottom player (loading it if needed).
    const playFile = useCallback(async (file, seekMs) => {
        if (!file) return;
        try {
            const track = await buildTrackObj(file);
            dispatch(resetAudioList());
            dispatch(addTrack(track));
            // Register the loaded list as the player's list context so playback
            // continues down the list when this track ends — auto-radio only
            // takes over after the LAST row (same behavior as the other tabs).
            // An active loop never ends the track, so it never advances mid-loop.
            setLocalListContext(dispatch, cueFiles, file, (p) => {
                const t = menuTrackFor(p);
                return { title: t.title, artist: t.artist, cover: t.artwork || '', djpalId: t.djpalId || null };
            });
            dispatch(setAutoPlay(true));
            setPlayingPath(file);
            setRowIsPlaying(true);
            if (seekMs != null) {
                setTimeout(() => { const a = window.musicPlayerInstance; if (a) a.currentTime = seekMs / 1000; }, 350);
            }
        } catch (e) { toast.error('Could not play track'); }
    }, [buildTrackObj, dispatch, toast, cueFiles, menuTrackFor]);

    const addFileToQueue = useCallback(async (file) => {
        try {
            const track = await buildTrackObj(file);
            addToNextUp(dispatch, track);
        } catch (e) { toast.error('Could not add to queue'); }
    }, [buildTrackObj, dispatch, toast]);

    const startPlayingSelected = useCallback((seekMs) => playFile(selectedFile, seekMs), [playFile, selectedFile]);

    // List auto-advance keeps the tab's core invariant — the waveform shows
    // what's playing (same coupling handleRowPlay enforces on a manual play).
    useEffect(() => {
        if (playingPath && cueFiles.includes(playingPath)) {
            dispatch(setSelectedCueFile(playingPath));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playingPath]);

    // Per-row play/pause toggle. Different track -> also open it in the waveform so
    // the playhead/loops apply to what's playing.
    const handleRowPlay = useCallback((file, e) => {
        e.stopPropagation();
        const a = window.musicPlayerInstance;
        if (playingPath === file && a) {
            if (autoPlay) { a.pause(); dispatch(setAutoPlay(false)); setRowIsPlaying(false); }
            else {
                a.muted = false; a.play().catch(() => {});
                // Re-assert the list context (see FixMyTracksTab): resuming
                // must restore play-through-the-list, not solo play.
                setLocalListContext(dispatch, cueFiles, file, (p) => {
                    const t = menuTrackFor(p);
                    return { title: t.title, artist: t.artist, cover: t.artwork || '', djpalId: t.djpalId || null };
                });
                dispatch(setAutoPlay(true)); setRowIsPlaying(true);
            }
        } else {
            dispatch(setSelectedCueFile(file));
            playFile(file);
        }
    }, [playingPath, autoPlay, dispatch, playFile, cueFiles, menuTrackFor]);

    const handleTogglePlay = useCallback(() => {
        const a = window.musicPlayerInstance;
        // The tab remounts on navigation (resetting playingPath), so also accept the
        // case where the bottom player already holds THIS track. Use the element's
        // real paused state, not the (possibly stale) redux autoPlay flag.
        const loaded = a && src && a.src === src;
        if (loaded || (isActiveTrack && a)) {
            if (!a.paused) { a.pause(); dispatch(setAutoPlay(false)); setRowIsPlaying(false); }
            else {
                a.muted = false;
                a.play().then(() => { dispatch(setAutoPlay(true)); setPlayingPath(selectedFile); }).catch(() => startPlayingSelected());
                dispatch(setAutoPlay(true)); setPlayingPath(selectedFile); setRowIsPlaying(true);
            }
        } else {
            startPlayingSelected();
        }
    }, [isActiveTrack, dispatch, startPlayingSelected, src, selectedFile]);

    // When Web Audio owns playback (loop or post-loop linear), the waveform watches
    // the bottom player and hands control back on any deliberate pause/seek. So all
    // we do here is drive the bottom player — the seek itself triggers the handback.
    const handleLoopExit = useCallback(() => {
        exitIntentRef.current = 'track';
        setActiveLoopId(null);
    }, []);

    const handleSeek = useCallback((ms) => {
        // ms is on the apps' timeline (cue list, pads, waveform callbacks); the
        // player decodes gapless, so play it that much earlier.
        const playMs = ms - (timelineOffsetByFile[selectedFile] || 0);
        const a = window.musicPlayerInstance;
        if (isActiveTrack && a) {
            a.currentTime = Math.max(0, playMs) / 1000;
            if (a.paused) { a.play().catch(() => {}); dispatch(setAutoPlay(true)); }
        } else { startPlayingSelected(Math.max(0, playMs)); }
    }, [isActiveTrack, startPlayingSelected, dispatch, timelineOffsetByFile, selectedFile]);

    // ---- editor callbacks ----
    const handleAddCue = (positionMs, forceMemory = false) => {
        if (!selectedFile) return;
        const usedHot = selectedCues.filter((c) => !isLoop(c) && c.hotcueIndex >= 0).map((c) => c.hotcueIndex);
        let hotIndex = -1;
        if (!forceMemory) {
            for (let i = 0; i < settings.maxHotCues; i++) { if (!usedHot.includes(i)) { hotIndex = i; break; } }
        }
        dispatch(addCue({
            filePath: selectedFile,
            cue: {
                id: _cueIdSeq++, type: 'cue', positionMs, endPositionMs: null,
                hotcueIndex: hotIndex, color: hotIndex >= 0 ? HOT_COLORS[hotIndex] : null, name: '',
                source: 'manual',
            },
        }));
        markDirty(selectedFile);
    };
    const handleMoveCue = (cueId, positionMs) => {
        if (!selectedFile) return;
        const idx = (cuesByFile[selectedFile] || []).findIndex((c) => c.id === cueId);
        // moving a cue by hand makes it "manual" so a re-analyze won't discard it
        if (idx >= 0) { dispatch(updateCue({ filePath: selectedFile, index: idx, patch: { positionMs, source: 'manual' } })); markDirty(selectedFile); }
    };
    const handleRemoveCue = (cueId) => {
        if (!selectedFile) return;
        const index = (cuesByFile[selectedFile] || []).findIndex((c) => c.id === cueId);
        if (index < 0) return;
        dispatch(removeCue({ filePath: selectedFile, index }));
        markDirty(selectedFile);
    };

    // ---- loop editor callbacks ----
    // Default new-loop length: match an existing loop on this track, else ~8s.
    const defaultLoopLenMs = () => {
        const existing = selectedCues.find((c) => isLoop(c) && c.endPositionMs > c.positionMs);
        return existing ? (existing.endPositionMs - existing.positionMs) : 8000;
    };
    const handleAddLoop = (startMs, endMs) => {
        if (!selectedFile) return;
        const usedSlots = selectedCues.filter((c) => isLoop(c) && c.hotcueIndex >= 0).map((c) => c.hotcueIndex);
        let slot = -1;
        for (let i = 0; i < LOOP_COLORS.length; i++) { if (!usedSlots.includes(i)) { slot = i; break; } }
        const start = Math.max(0, Math.round(startMs));
        const end = Math.round(endMs != null ? endMs : start + defaultLoopLenMs());
        dispatch(addCue({
            filePath: selectedFile,
            cue: {
                id: _cueIdSeq++, type: 'loop', positionMs: start, endPositionMs: Math.max(start + 250, end),
                hotcueIndex: slot, color: slot >= 0 ? LOOP_COLORS[slot] : LOOP_COLORS[0], name: 'Loop',
                source: 'manual',
            },
        }));
        markDirty(selectedFile);
    };
    // Move a whole loop, preserving its length; editing retags it manual.
    const handleMoveLoop = (cueId, newStartMs) => {
        if (!selectedFile) return;
        const list = cuesByFile[selectedFile] || [];
        const idx = list.findIndex((c) => c.id === cueId);
        if (idx < 0) return;
        const len = (list[idx].endPositionMs || 0) - list[idx].positionMs;
        const start = Math.max(0, Math.round(newStartMs));
        dispatch(updateCue({ filePath: selectedFile, index: idx, patch: { positionMs: start, endPositionMs: start + len, source: 'manual' } }));
        markDirty(selectedFile);
    };
    // Drag one edge of a loop ('start' | 'end'); keeps start < end, retags manual.
    const handleResizeLoop = (cueId, edge, ms) => {
        if (!selectedFile) return;
        const list = cuesByFile[selectedFile] || [];
        const idx = list.findIndex((c) => c.id === cueId);
        if (idx < 0) return;
        const cue = list[idx];
        const patch = { source: 'manual' };
        if (edge === 'start') patch.positionMs = Math.min(Math.max(0, Math.round(ms)), (cue.endPositionMs || 0) - 250);
        else patch.endPositionMs = Math.max(cue.positionMs + 250, Math.round(ms));
        dispatch(updateCue({ filePath: selectedFile, index: idx, patch }));
        markDirty(selectedFile);
    };

    // ×2 / ÷2 a loop's length (start fixed). Clamps to [~1s, track end].
    const handleScaleLoop = (cueId, factor) => {
        if (!selectedFile) return;
        const list = cuesByFile[selectedFile] || [];
        const idx = list.findIndex((c) => c.id === cueId);
        if (idx < 0) return;
        const cue = list[idx];
        const len = (cue.endPositionMs || cue.positionMs) - cue.positionMs;
        const durMs = (durationsByFile[selectedFile] || 0) * 1000;
        let newEnd = cue.positionMs + Math.max(1000, Math.round(len * factor));
        if (durMs) newEnd = Math.min(newEnd, Math.round(durMs));
        if (newEnd <= cue.positionMs + 250) return;
        // Length scales by the factor, so the bar count scales the same way.
        const newBars = cue.bars ? Math.max(1, Math.round(cue.bars * factor)) : null;
        const patch = { endPositionMs: newEnd, source: 'manual' };
        if (newBars) { patch.bars = newBars; patch.name = `${newBars} bars`; }
        dispatch(updateCue({ filePath: selectedFile, index: idx, patch }));
        markDirty(selectedFile);
    };
    // Toggle gapless loop playback for a loop (one active at a time). The actual
    // sample-accurate looping is done in ColoredWaveform via the Web Audio API.
    const handleToggleLoopActive = (cueId) => {
        if (activeLoopId === cueId) {
            // turning OFF — the waveform continues the loop node linearly and
            // crossfades back to the bottom player (seamless). Just flag the intent.
            exitIntentRef.current = 'resume';
            setActiveLoopId(null);
        } else {
            // turning ON — the waveform loops via Web Audio (muting the bottom
            // player). Reflect that the loop is audibly playing.
            dispatch(setAutoPlay(true));
            setActiveLoopId(cueId);
        }
    };

    // Rekordbox Active Loop (max ONE per track — CDJs auto-engage it when
    // playback reaches it). Manual per-track pick; null = none (the global
    // "Auto Active Loop" setting can still arm the last loop at save time).
    const handleSetRbActiveLoop = (loopId) => {
        if (!selectedFile) return;
        const list = cuesByFile[selectedFile] || [];
        list.forEach((c, idx) => {
            if (!isLoop(c)) return;
            const want = loopId != null && c.id === loopId;
            if (!!c.activeLoop !== want) {
                dispatch(updateCue({ filePath: selectedFile, index: idx, patch: { activeLoop: want } }));
            }
        });
        markDirty(selectedFile);
    };

    // Split the current track's items into cues vs loops for the editor + pads.
    const onlyCues = useMemo(() => selectedCues.filter((c) => !isLoop(c)), [selectedCues]);
    const onlyLoops = useMemo(() => selectedCues.filter(isLoop), [selectedCues]);
    // Reading the selected track's embedded cues from disk (Python subprocess) —
    // distinct from "read finished and there are genuinely none".
    const selectedReading = !!selectedFile && (readingFiles.has(selectedFile) || analyzingFiles.has(selectedFile));
    const rbActiveLoopId = useMemo(() => (onlyLoops.find((l) => l.activeLoop === true) || {}).id ?? null, [onlyLoops]);

    // Right-click menu on a loop pad → set/clear the Rekordbox Active Loop.
    // Rekordbox-only concept, so the menu only exists when RB is a write target.
    const [loopMenu, setLoopMenu] = useState(null); // { x, y, loopId, isActive } | null
    const loopMenuRef = useRef(null);
    const [loopMenuPos, setLoopMenuPos] = useState(null); // clamped {left, top}; null until measured
    const handleLoopContextMenu = settings.targetRekordbox
        ? (e, loop) => { setLoopMenuPos(null); setLoopMenu({ x: e.clientX, y: e.clientY, loopId: loop.id, isActive: loop.id === rbActiveLoopId }); }
        : null;
    // Flip/clamp the loop menu so it stays on-screen when opened near an edge.
    useLayoutEffect(() => {
        if (!loopMenu || !loopMenuRef.current) { setLoopMenuPos(null); return; }
        setLoopMenuPos(positionMenuAtPoint(loopMenuRef.current, loopMenu.x, loopMenu.y));
    }, [loopMenu]);

    // Drop the active loop if its track is deselected or the loop is gone.
    useEffect(() => {
        if (activeLoopId != null && !onlyLoops.some((l) => l.id === activeLoopId)) { exitIntentRef.current = 'track'; setActiveLoopId(null); }
    }, [activeLoopId, onlyLoops]);

    // Keyboard pads: 1–8 jump to that hot cue, A–H toggle that loop.
    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            const inField = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable);
            const mod = e.metaKey || e.ctrlKey;
            // Undo / redo — Cmd/Ctrl+Z, redo = Shift+Cmd/Ctrl+Z or Ctrl+Y (not while typing)
            if (mod && !inField && (e.key === 'z' || e.key === 'Z')) {
                e.preventDefault();
                dispatch(e.shiftKey ? redoCues() : undoCues());
                markDirty(cueFiles);
                return;
            }
            if (e.ctrlKey && !inField && (e.key === 'y' || e.key === 'Y')) {
                e.preventDefault();
                dispatch(redoCues());
                markDirty(cueFiles);
                return;
            }
            if (inField || e.metaKey || e.ctrlKey || e.altKey) return;
            if (!selectedFile) return;
            const k = e.key;
            if (k >= '1' && k <= '8') {
                const cue = onlyCues.find((c) => c.hotcueIndex === parseInt(k, 10) - 1);
                if (cue) { e.preventDefault(); handleSeek(cue.positionMs); }
            } else if (/^[a-hA-H]$/.test(k)) {
                const loop = onlyLoops.find((l) => l.hotcueIndex === k.toUpperCase().charCodeAt(0) - 65);
                if (loop) { e.preventDefault(); handleToggleLoopActive(loop.id); }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const baseName = (p) => (p ? p.split('/').pop() : '');
    const displayName = (p) => {
        const m = metaByFile[p];
        if (m && m.title) return m.artist ? `${m.artist} – ${m.title}` : m.title;
        // the local DB's names (what the Library shows), cleaned the same way
        if ((enrichedMap.get(p) || {}).title) {
            const e = getEnrichedParts(enrichedMap, p);
            const showArtist = e.artist && e.artist !== 'Unknown Artist'
                && !e.title.toLowerCase().startsWith(`${e.artist.toLowerCase()} - `);
            return showArtist ? `${e.artist} – ${e.title}` : e.title;
        }
        return baseName(p).replace(/\.[^/.]+$/, '');
    };
    const { totalCues, totalLoops } = useMemo(() => {
        let c = 0; let l = 0;
        for (const f of cueFiles) {
            for (const item of (cuesByFile[f] || [])) { if (isLoop(item)) l++; else c++; }
        }
        return { totalCues: c, totalLoops: l };
    }, [cueFiles, cuesByFile]);
    const totalItems = totalCues + totalLoops;
    // The per-row counts column renders only once SOME row has data (or an
    // error) — before any generation it is just a hole between the play button
    // and the title. Once one track has counts, every row shows the column
    // (fixed sub-slots) so titles stay aligned; an empty cell then honestly
    // reads as "not generated yet".
    // Reads kick off the moment files load, so counting in-flight reads here
    // reserves the counts column from first paint — counts arriving 1-2s later
    // must fill space that's already there, not push every track name right.
    const anyRowCounts = totalItems > 0 || readingFiles.size > 0 || analyzingFiles.size > 0 || cueFiles.some((f) => errorsByFile[f]);

    const openButton = (
        <>
            <Dropdown>
                <Dropdown.Toggle className={v2 ? 'v2-btn v2-source-chip' : 'button choose-button'} id="cue-open-dropdown" ref={playlistBtnRef}>
                    {v2 ? (
                        <>
                            <AudioFileIcon className="icon" />
                            <span className="v2-source-label">{cueFiles.length ? 'Tracks' : 'Open'}</span>
                            {cueFiles.length > 0 && <span className="v2-faint v2-num">· {cueFiles.length}</span>}
                        </>
                    ) : (
                        <><OpenIcon className="icon" /> Open</>
                    )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={handleChooseFiles}>Files…</Dropdown.Item>
                    <Dropdown.Item onClick={handleChooseDir}>Folder…</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handlePlaylistDropdownToggle(playlistBtnRef.current)}>Playlist</Dropdown.Item>
                    <Dropdown.Item onClick={handleChooseLibrary}>My Library</Dropdown.Item>
                    {v2 && cueFiles.length > 0 && (
                        <>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handleClear}>Clear list</Dropdown.Item>
                        </>
                    )}
                </Dropdown.Menu>
            </Dropdown>
            {playlistDropdownOpen && (
                <div ref={playlistDropdownRef} className="smart-recs-dropdown-menu" style={{
                    position: 'fixed',
                    top: playlistDropdownPos.top,
                    left: playlistDropdownPos.left,
                    width: playlistDropdownPos.width,
                    zIndex: 1000,
                }}>
                    <div className="dropdown-search-wrapper">
                        <svg className="dropdown-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input
                            ref={playlistSearchRef}
                            className="dropdown-search-input"
                            type="text"
                            placeholder="Search playlists..."
                            value={playlistSearch}
                            onChange={(e) => setPlaylistSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="dropdown-options">
                        {playlistsLoading ? (
                            <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                        ) : (
                            <>
                                {playlistList
                                    .filter((pl) => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase()))
                                    .map((pl) => (
                                        <button
                                            key={pl.id}
                                            className="dropdown-option"
                                            onClick={() => handlePlaylistSelect(pl)}
                                        >
                                            <span className="dropdown-option-name">{pl.name}</span>
                                        </button>
                                    ))
                                }
                                {playlistList.filter((pl) => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 && (
                                    playlistList.length === 0 ? (
                                        <div className="dropdown-no-results" style={{ whiteSpace: 'normal', lineHeight: 1.4, textAlign: 'left', padding: '10px 12px' }}>
                                            No playlists yet. These tools work on audio files on this computer — import a library (Rekordbox, Serato, Traktor, VirtualDJ) or add local tracks.
                                        </div>
                                    ) : (
                                        <div className="dropdown-no-results">No playlists match your search</div>
                                    )
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );

    // Experimental downbeat compare (dev flag): swap every generated track's
    // shown placement between the two downbeat modes without re-analyzing.
    const variantsByFile = useSelector((state) => state.cuePoints.variantsByFile || {});
    const shownVariant = useSelector((state) => state.cuePoints.shownVariant || 'merged');
    const hasVariants = Object.keys(variantsByFile).length > 0;
    const switchVariant = (mode) => {
        if (mode === shownVariant) return;
        const cueMode = settings.cueMode || 'hot';
        const clampedMaxHot = (settings.maxHotCues >= 1 && settings.maxHotCues <= 8) ? settings.maxHotCues : 8;
        const effectiveMaxHot = cueMode === 'memory' ? 0 : clampedMaxHot;
        const updates = [];
        for (const [f, byMode] of Object.entries(variantsByFile)) {
            const generated = byMode && byMode[mode];
            if (!generated) continue;
            const existing = (cuesByFileRef.current[f] || []).filter((c) => c.source !== 'auto');
            updates.push({ filePath: f, cuePoints: alignLoopsToCues(labelCuesFromLoops(mergeCues(existing, withIds(generated), effectiveMaxHot, cueMode === 'memory'))) });
        }
        if (updates.length) {
            dispatch(pushCueUndo());
            dispatch(setBatchResults(updates));
            markDirty(updates.map((u) => u.filePath));
        }
        dispatch(setShownVariant(mode));
        trackEvent('cue_points_downbeat_variant', { mode, count: updates.length });
    };
    const variantSwitch = (settings.downbeatCompare && hasVariants) ? (
        <div className="cue-variant-switch" title="Dev: which downbeat vote placed the loops and snapped the cues. Same analysis, two placements — New = merged vote (default), Previous = kick-flux vote.">
            <span className="cue-variant-label">Grid</span>
            <button type="button" className={`cue-variant-btn${shownVariant === 'merged' ? ' active' : ''}`} onClick={() => switchVariant('merged')}>New</button>
            <button type="button" className={`cue-variant-btn${shownVariant === 'kick' ? ' active' : ''}`} onClick={() => switchVariant('kick')}>Previous</button>
        </div>
    ) : null;

    const genWhat = genMode === 'both' ? 'cues & loops' : genMode === 'loops' ? 'loops' : 'cues';
    const hasWhat = genMode === 'loops' ? 'loops' : 'cues';   // what makes a track "done" (cueRunPlan)
    const skippedN = cueFiles.length - remaining.length;
    const generateTitle = !cueFiles.length ? 'Detect cues & loops'
        : !generateAll ? `Skips the ${skippedN} ${skippedN === 1 ? 'track that already has' : 'tracks that already have'} ${hasWhat}`
            : remaining.length === 0 ? `Every track already has ${hasWhat} — runs them all again`
                : `Detect ${genWhat} for all ${cueFiles.length} loaded ${cueFiles.length === 1 ? 'track' : 'tracks'}`;
    const actionButtons = (
        <div className="cue-actions">
            {openButton}
            {variantSwitch}
            <Button className="button analyze-button main-button" onClick={handleGenerate}
                    disabled={isProcessing || resolving || cueFiles.length === 0}
                    title={generateTitle}>
                <AnalyzeIcon className="icon" />
                {!cueFiles.length ? 'Generate' : generateAll ? `Generate all (${cueFiles.length})` : `Generate remaining (${remaining.length})`}
            </Button>
            <Button className="button main-button" onClick={handleSave}
                    disabled={saving || resolving || cueFiles.length === 0}
                    title={!cueFiles.length ? 'Save' : isProcessing && !saving
                        ? 'Save the tracks finished so far — the run keeps going'
                        : `Save cues & loops for all ${cueFiles.length} loaded ${cueFiles.length === 1 ? 'track' : 'tracks'}`}>
                <SaveIcon className="icon" />
                {saving ? 'Saving…' : 'Save'}
            </Button>
        </div>
    );

    // ---- DESIGN V2: the list's tool header ----
    const fmtDur = (sec) => { if (!sec || isNaN(sec)) return ''; const m = Math.floor(sec / 60); const s2 = Math.floor(sec % 60); return `${m}:${String(s2).padStart(2, '0')}`; };
    const countSentence = readTotal > 50 && readingFiles.size > 0
        ? <>Reading saved cues · <b>{(readTotal - readingFiles.size).toLocaleString()}</b> of <b>{readTotal.toLocaleString()}</b></>
        : metaProgress
            ? <>Reading tags · <b>{metaProgress.done.toLocaleString()}</b> of <b>{metaProgress.total.toLocaleString()}</b></>
            : totalItems > 0
                ? <><b>{cueFiles.length}</b> {cueFiles.length === 1 ? 'track' : 'tracks'} · <b>{totalCues}</b> cues{totalLoops ? <> · <b>{totalLoops}</b> loops</> : null}</>
                : <><b>{cueFiles.length}</b> {cueFiles.length === 1 ? 'track' : 'tracks'} to analyze</>;
    const v2ListHeader = v2 && (
        <ToolHeader
            className="v2-tool-cues"
            source={openButton}
            stats={(isProcessing || resolving)
                ? <ProgressBarWithStop isProcessing={isProcessing || resolving} progress={progress} handleStopProcessing={handleStop} v2 stage={resolving ? 'Reading files…' : progressMessage} />
                : cueFiles.length === 0
                ? null /* the empty page below already says it, as on the other tools */
                : <span className="cue-file-count" title="Generate runs the tracks still without cues; Save writes every loaded track">{countSentence}</span>}
            settings={<CueSettingsPopoverV2 />}
            actions={(
                <>
                    {variantSwitch}
                    {/* DESIGN V2: nothing loaded, nothing to save or generate; the empty state's pill opens a source. */}
                    {cueFiles.length > 0 && <>
                    <button type="button" className="v2-btn" onClick={handleSave}
                        disabled={saving || resolving || cueFiles.length === 0}
                        title={!cueFiles.length ? 'Save' : isProcessing && !saving
                            ? 'Save the tracks finished so far — the run keeps going'
                            : `Save cues & loops for all ${cueFiles.length} loaded ${cueFiles.length === 1 ? 'track' : 'tracks'}`}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="v2-btn primary analyze-button" onClick={handleGenerate}
                        disabled={isProcessing || resolving || cueFiles.length === 0}
                        title={generateTitle}>
                        {!cueFiles.length ? 'Generate' : generateAll ? `Generate all (${cueFiles.length})` : `Generate remaining (${remaining.length})`}
                    </button>
                    </>}
                </>
            )}
        />
    );

    return (
        <div
            className={`tab-container cue-points-tab ${v2 ? 'v2-tool-page ' : ''}${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* DESIGN V2: progress lives in the tool header (below), like the other tools;
                this strip above the editor pushed the whole page down while it ran. */}
            {!v2 && (isProcessing || resolving) && (
                <div className="cue-progress-row">
                    <ProgressBarWithStop isProcessing={isProcessing} progress={progress} handleStopProcessing={handleStop} v2={v2} />
                    {(progressMessage || resolving) && (
                        <div className="cue-progress-msg">{resolving ? 'Reading files…' : progressMessage}</div>
                    )}
                </div>
            )}

            {cueFiles.length === 0 ? (
                /* ---- empty state: drop zone + settings ---- */
                <div className="cue-empty-layout">
                    <div className="cue-empty-main">
                        {v2ListHeader}
                        {!v2 && <div className="upper-part source-bar-header cue-empty-bar">
                            <div className="source-bar">
                                <div className="source-bar-left">
                                    <AudioFileIcon className="source-bar-icon" />
                                    <span className="source-bar-title empty">No tracks loaded — drop files or open to start</span>
                                </div>
                                <div className="source-bar-actions">{actionButtons}</div>
                            </div>
                        </div>}
                        <div className="cue-dropzone" onClick={handleChooseDir}>
                            {/* DESIGN V2: the folder every other tool shows. */}
                            {v2 ? <DirectoryIcon className="cue-empty-icon" /> : <AudioFileIcon className="cue-empty-icon" />}
                            <span>Drop tracks or folders here, or click <strong>Open</strong>.</span>
                        </div>
                    </div>
                    {!v2 && <CuePointsSettings />}
                </div>
            ) : (
                <>
                    {/* ---- editor: header + waveform + cue pads ---- */}
                    <div className="cue-editor-section">
                        <div className="cue-editor-header">
                            <div className="cue-editor-title">
                                <span className="cue-editor-name">{selectedFile ? displayName(selectedFile) : '—'}</span>
                                <span className="cue-editor-count">
                                    {onlyCues.length} {onlyCues.length === 1 ? 'cue' : 'cues'}
                                    {onlyLoops.length > 0 && ` · ${onlyLoops.length} ${onlyLoops.length === 1 ? 'loop' : 'loops'}`}
                                    {v2 && selectedFile && durationsByFile[selectedFile] > 0 && ` · ${fmtDur(durationsByFile[selectedFile])}`}
                                </span>
                            </div>
                            {selectedFile && (
                                <div className="cue-editor-tools">
                                    <div className="cue-undo-group" role="group" aria-label="Undo / redo">
                                        <button
                                            type="button"
                                            className="cue-undo-btn"
                                            disabled={!canUndo}
                                            onClick={() => { dispatch(undoCues()); markDirty(cueFiles); }}
                                            title="Undo (⌘Z)"
                                        >
                                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="cue-undo-btn"
                                            disabled={!canRedo}
                                            onClick={() => { dispatch(redoCues()); markDirty(cueFiles); }}
                                            title="Redo (⇧⌘Z)"
                                        >
                                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 14l5-5-5-5" /><path d="M20 9H9a5 5 0 0 0 0 10h3" /></svg>
                                        </button>
                                    </div>
                                    {v2 && <span className="v2-zoom-slot" ref={zoomSlotRef} />}
                                    {onlyCues.length > 0 && onlyLoops.length > 0 && (
                                        <div className="cue-view-toggle" role="group" aria-label="Show layers">
                                            <button
                                                type="button"
                                                className={`cue-view-pill ${showCues ? 'active' : ''}`}
                                                onClick={() => setShowCues((v) => !v)}
                                                title="Show / hide cues"
                                            >
                                                Cues
                                            </button>
                                            <button
                                                type="button"
                                                className={`cue-view-pill loops ${showLoops ? 'active' : ''}`}
                                                onClick={() => setShowLoops((v) => !v)}
                                                title="Show / hide loops"
                                            >
                                                Loops
                                            </button>
                                        </div>
                                    )}
                                    {/* DESIGN V2: outlined, not amber: the list header's Generate is the
                                        page's one main action; this one is for the open track only. */}
                                    <button
                                        className={v2 ? 'v2-btn sm cue-track-generate' : 'cue-track-generate'}
                                        onClick={(e) => handleRowGenerate(selectedFile, e)}
                                        disabled={isProcessing}
                                        title={`Generate ${settings.detectLoops ? 'cues & loops' : 'cues'} for this track only`}
                                    >
                                        {!v2 && <AnalyzeIcon className="icon" />}
                                        Generate
                                    </button>
                                </div>
                            )}
                        </div>

                        {selectedFile && (
                            <ColoredWaveform
                                key={selectedFile}
                                src={src}
                                cues={showCues ? onlyCues : []}
                                loops={showLoops ? onlyLoops : []}
                                activeLoopId={activeLoopId}
                                timelineOffsetMs={timelineOffsetByFile[selectedFile] || 0}
                                loopPosRef={loopPosRef}
                                audioLatencyRef={audioLatencyRef}
                                exitIntentRef={exitIntentRef}
                                onLoopExit={handleLoopExit}
                                hoveredId={hoveredId}
                                onHoverItem={setHoveredId}
                                onAddCue={handleAddCue}
                                onMoveCue={handleMoveCue}
                                onAddLoop={handleAddLoop}
                                onMoveLoop={handleMoveLoop}
                                onResizeLoop={handleResizeLoop}
                                isActive={isActiveTrack}
                                isPlaying={isPlaying}
                                onTogglePlay={handleTogglePlay}
                                onSeek={handleSeek}
                                twoTone={false}
                                zoomSlotRef={v2 ? zoomSlotRef : null}
                            >
                                <CueList
                                    cues={showCues ? onlyCues : []}
                                    loops={showLoops ? onlyLoops : []}
                                    loading={selectedReading}
                                    hoveredId={hoveredId}
                                    onHoverItem={setHoveredId}
                                    onRemove={handleRemoveCue}
                                    onSeek={handleSeek}
                                    onScaleLoop={handleScaleLoop}
                                    activeLoopId={activeLoopId}
                                    onToggleLoopActive={handleToggleLoopActive}
                                    rbActiveLoopId={rbActiveLoopId}
                                    onToggleRbActive={settings.targetRekordbox
                                        ? (loop) => handleSetRbActiveLoop(loop.id === rbActiveLoopId ? null : loop.id)
                                        : null}
                                    onLoopContextMenu={handleLoopContextMenu}
                                />
                            </ColoredWaveform>
                        )}
                    </div>

                    {/* ---- file list (with action toolbar) + settings ---- */}
                    <div className="cue-lower">
                        <div className="cue-file-list" ref={listContainerRef}>
                            {v2ListHeader}
                            {!v2 && <div className="cue-list-toolbar">
                                {actionButtons}
                                <span className="cue-file-count" title="Generate runs the tracks still without cues; Save writes every loaded track">
                                    {/* what's still being read shows here first — otherwise
                                        a big open looks stuck: filenames, no counts, no word */}
                                    {readTotal > 50 && readingFiles.size > 0
                                        ? `Reading saved cues · ${(readTotal - readingFiles.size).toLocaleString()} of ${readTotal.toLocaleString()}`
                                        : metaProgress
                                            ? `Reading tags · ${metaProgress.done.toLocaleString()} of ${metaProgress.total.toLocaleString()}`
                                            : totalItems > 0
                                                ? `${cueFiles.length} ${cueFiles.length === 1 ? 'track' : 'tracks'} · ${totalCues} cues${totalLoops ? ` · ${totalLoops} loops` : ''}`
                                                : `${cueFiles.length} ${cueFiles.length === 1 ? 'track' : 'tracks'} to analyze`}
                                </span>
                                <button className="cue-clear-btn" onClick={handleClear} title="Clear all">&times;</button>
                            </div>}
                            <List
                                listRef={cueListRef}
                                className="cue-file-rows"
                                style={{ flex: '1 1 auto', minHeight: 0, width: '100%' }}
                                rowCount={cueFiles.length}
                                rowHeight={CUE_ROW_HEIGHT}
                                overscanCount={8}
                                rowProps={{
                                    files: cueFiles, cuesByFile, errorsByFile, selectedFile, selectedRows,
                                    readingFiles, analyzingFiles, anyRowCounts, displayName, enrichedMap,
                                    gridRepairByFile, playingPath, rowIsPlaying, rowCtx, menuTrackFor,
                                    onRowClick: handleRowClick, onRowContextMenu: handleRowContextMenu,
                                    onRowPlay: handleRowPlay, onAddToQueue: addFileToQueue, onRemove: removeFilesFromList,
                                }}
                                rowComponent={CueFileRow}
                            />
                        </div>
                        {!v2 && <CuePointsSettings />}
                    </div>
                </>
            )}

            {isDragging && (
                <div className="cue-drop-overlay"><span>Drop audio files or folders</span></div>
            )}

            {loopMenu && ReactDOM.createPortal(
                <div
                    className="loop-ctx-overlay"
                    onClick={() => setLoopMenu(null)}
                    onContextMenu={(e) => { e.preventDefault(); setLoopMenu(null); }}
                >
                    <div
                        ref={loopMenuRef}
                        className="loop-ctx-menu"
                        style={{
                            left: loopMenuPos ? loopMenuPos.left : loopMenu.x,
                            top: loopMenuPos ? loopMenuPos.top : loopMenu.y,
                            visibility: loopMenuPos ? 'visible' : 'hidden',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="loop-ctx-item"
                            onClick={() => { handleSetRbActiveLoop(loopMenu.isActive ? null : loopMenu.loopId); setLoopMenu(null); }}
                        >
                            <LoopIcon className="loop-ctx-ico" />
                            {loopMenu.isActive ? 'Clear Active Loop' : 'Set as Active Loop'}
                        </button>
                        <div className="loop-ctx-hint">
                            Rekordbox: CDJs auto-engage the Active Loop when playback reaches it — one per track.
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {bulkCtx && orderedSelected.length > 1 && (
                <BulkRowContextMenu
                    x={bulkCtx.x}
                    y={bulkCtx.y}
                    onClose={() => setBulkCtx(null)}
                    analyticsFrom="cue_points"
                    count={orderedSelected.length}
                    items={[
                        {
                            label: `Add ${orderedSelected.length} Tracks to Playlist`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
                            onClick: () => setBulkPlaylistTargets(orderedSelected),
                        },
                        { divider: true },
                        {
                            label: `Remove ${orderedSelected.length} Tracks from list`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>,
                            danger: true,
                            onClick: () => removeFilesFromList(orderedSelected, 'menu'),
                        },
                    ]}
                />
            )}
            {bulkPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setBulkPlaylistTargets([])}
                    bulkFilePaths={bulkPlaylistTargets}
                    initialTab="add"
                    bulkLabel={`${bulkPlaylistTargets.length} tracks`}
                />
            )}
        </div>
    );
};

export default CuePointsTab;
